﻿import {Locale, DateOnly, eventMatches, WeekInfo} from "@2d-front-monorepo/core";
import {JSX, useCallback, useEffect, useMemo, useRef, useState} from "react";
import Icon from "../Icon";
import {getDateInfo, normalizeYearMonth} from "./getDateInfo";

function convertDay(day: number): number {
	return day == 7 ? 0 : day;
}

interface CalendarInformation {
	current: DateOnly;
	firstDay: DateOnly;
	lastDay: DateOnly;
	firstVisibleDay: DateOnly;
	lastVisibleDay: DateOnly;
}

interface CalendarCellData {
	date: DateOnly;
	currentMonth: DateOnly;
}

interface RenderCellProps {
	cellData: CalendarCellData;
	selected: DateOnly;
	today: DateOnly;
	info: WeekInfo;
	isSelectable?: (date: DateOnly) => boolean;
	dayClassName?: (date: DateOnly) => string | undefined;
	culture: Locale;
}

export interface CalendarProps {
	selected?: DateOnly;
	onSelect?: (date: DateOnly) => void;
	isSelectable?: (date: DateOnly) => boolean;
	dayClassName?: (date: DateOnly) => string | undefined;
	minValue?: DateOnly;
	maxValue?: DateOnly;
}

function RenderCell(props: RenderCellProps): JSX.Element {
	const {cellData, info, today, selected, isSelectable, dayClassName, culture} = props;
	const {date, currentMonth} = cellData;

	const year = date.getYear();
	const month = date.getMonth();
	const day = date.getDay();

	const cm = month == currentMonth.getMonth() && year == currentMonth.getYear();

	const cellRef = useRef<HTMLDivElement>(null);

	const isWeekend = info.weekend.includes(date.getDayOfWeek());
	const isToday = year === today.getYear() && month === today.getMonth() && day === today.getDay();
	const isSelected = year === selected.getYear() && month === selected.getMonth() && day === selected.getDay();
	const dayCL = dayClassName && dayClassName(date);

	const classNames = [
		"calendar__cell",
		cm ? "" : "calendar__cell--blurred",
		isWeekend ? "calendar__cell--weekend" : "",
		isToday ? "calendar__cell--today" : "",
		isSelected ? "calendar__cell--selected" : "",
		isSelectable && !isSelectable(date) ? "calendar__cell--disabled" : "",
		dayCL ? dayCL : ""
	].filter(Boolean).join(" ");

	return (
		<div className={classNames} data-date={`${date.toJSON().split("T")[0]}`} ref={cellRef}>
			<span className="calendar__cell--date">{culture.formatNumber(day, "integer")}</span>
		</div>
	);
}

function RenderDayNames({culture}: { culture: Locale }): JSX.Element {
	const days = culture.shortDayNames;
	return <>{days.map((day) => <div key={day.label} className="calendar__day-name">{day.label}</div>)}</>;
}

export default function Calendar(props: CalendarProps): JSX.Element {
	const {selected, onSelect, isSelectable, dayClassName, minValue, maxValue} = props;
	const [info, setInfo] = useState<CalendarInformation>(null!);
	const culture = Locale.getCurrent();

	const today = useMemo(() => DateOnly.today(), []);
	const weekInfo = useMemo(() => {
		const wi = culture.getWeekInfo();
		return {
			firstDay: convertDay(wi.firstDay),
			weekend: wi.weekend.map(convertDay),
			minimalDays: wi.minimalDays
		};
	}, [culture]);

	const update = useCallback((newDate: DateOnly) => {
		const dow = weekInfo.firstDay;
		const year = newDate.getYear();
		const month = newDate.getMonth();

		const previousMonth = getDateInfo(year, month - 1, dow);
		const currentMonth = getDateInfo(year, month, dow);
		const nextMonth = getDateInfo(year, month + 1, dow);

		const firstVisibleDay = previousMonth.lastOfMonth - currentMonth.offset;
		const lastVisibleDay = 6 * 7 - (currentMonth.offset + currentMonth.lastOfMonth);

		setInfo({
			current: newDate,
			firstVisibleDay: DateOnly.fromComponents(previousMonth.year, previousMonth.month, firstVisibleDay + 1),
			lastVisibleDay: DateOnly.fromComponents(nextMonth.year, nextMonth.month, lastVisibleDay),
			firstDay: DateOnly.fromComponents(currentMonth.year, currentMonth.month, 1),
			lastDay: DateOnly.fromComponents(currentMonth.year, currentMonth.month, currentMonth.lastOfMonth)
		});
	}, [weekInfo.firstDay]);

	const isInRange = useCallback(function (date: DateOnly): boolean {
		return (minValue == null || minValue <= date) && (maxValue == null || date <= maxValue);
	}, [minValue, maxValue]);

	const moveCalendar = useCallback((yearOffset: number, monthOffset: number) => {
		const c = info.current;
		const [year, month] = normalizeYearMonth(c.getYear() + yearOffset, c.getMonth() + monthOffset);
		const newDate = DateOnly.fromComponents(year, month, 1);

		update(newDate);

	}, [update, info]);

	const isSelectableInternal = useCallback(function (date: DateOnly): boolean {
		return isInRange(date) && (isSelectable == null || isSelectable(date));
	}, [isInRange, isSelectable]);

	const cellsData = useMemo(() => {
		if (!info) {
			return [];
		}

		const result: CalendarCellData[] = [];
		const totalDays = 6 * 7; // 6 rows, 7 days per week

		const currentMonth = DateOnly.fromComponents(info.firstDay.getYear(), info.firstDay.getMonth(), 1);
		let currentDate = info.firstVisibleDay;

		for (let i = 0; i < totalDays; i++) {
			result.push({
				date: currentDate,
				currentMonth
			});

			currentDate = currentDate.addDays(1);
		}

		return result;
	}, [info]);

	const onClick = useCallback(function (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
		event.preventDefault();
		if (!onSelect) {
			return;
		}

		const clicked = eventMatches(event.nativeEvent, "div[data-date]");
		if (!clicked) {
			return;
		}

		if (clicked.classList.contains("calendar__cell--disabled")) {
			return;
		}

		onSelect(DateOnly.fromIsoString(clicked.dataset["date"]!));
	}, [onSelect]);

	const onKeyDown = useCallback(function (event: React.KeyboardEvent<HTMLDivElement>): void {
		if (event.key !== "Enter" && event.key !== " ") {
			return;
		}

		event.preventDefault();
		if (!onSelect) {
			return;
		}

		const target = event.target as HTMLElement;
		const cell = target.closest("div[data-date]");
		if (!cell || cell.classList.contains("calendar__cell--disabled")) {
			return;
		}

		onSelect(DateOnly.fromIsoString((cell as HTMLElement).dataset["date"]!));
	}, [onSelect]);

	useEffect(() => {
		if (!selected) {
			return;
		}
		update(selected);
	}, [selected, update]);

	if (!info || !info.current || !selected) {
		return <></>;
	}

	return <div className="calendar">
		<div className="calendar__navigation">
			<span>{culture.longMonthNames[info.current.getMonth() - 1]} {info.current.getYear()}</span>
			<button type="button" onClick={() => moveCalendar(-1, 0)}>
				<Icon icon="calendar-prev-year"/>
			</button>
			<button type="button" onClick={() => moveCalendar(0, -1)}>
				<Icon icon="calendar-prev-month"/>
			</button>
			<button type="button" onClick={() => update(today)}>
				<Icon icon="calendar-today"/>
			</button>
			<button type="button" onClick={() => moveCalendar(0, 1)}>
				<Icon icon="calendar-next-month"/>
			</button>
			<button type="button" onClick={() => moveCalendar(1, 0)}>
				<Icon icon="calendar-next-year"/>
			</button>
		</div>
		<div className="calendar__header">
			<RenderDayNames culture={culture}/>
		</div>
		<div className="calendar__body" role="grid" onClick={onClick} onKeyDown={onKeyDown}>
			{cellsData.map((cellData) => <RenderCell
				key={cellData.date.toJSON()}
				culture={culture}
				cellData={cellData}
				info={weekInfo}
				today={today}
				selected={selected}
				isSelectable={isSelectableInternal}
				dayClassName={dayClassName}
			/>)}
		</div>
	</div>;
}
